import React, { useMemo, useState, useRef, useEffect } from 'react'
import { 
    Button, 
    Col, 
    Form, 
    Input, 
    notification, 
    Row, 
    Upload, 
    Card, 
    Avatar, 
    Space, 
    Tooltip,
    Divider
} from 'antd';
import { AddUser_User_Api, GetCurrentRole_User_Api } from '../../API/userApi';
import { useNavigate } from "react-router-dom";
import {
    UploadOutlined,
    DeleteOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    UserOutlined,
    IdcardOutlined,
    PhoneOutlined,
    HomeOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';

// وارد کردن DatePicker و تنظیمات شمسی (همانند کد موجود شما)
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

// ایمپورت تصویر
import loginImage from '../../assets/img/loginPage.png';

const { TextArea } = Input;
const Context = React.createContext({ name: 'Default' });

// =================================================================
const SERVER_DATE_FORMAT = "YYYY-MM-DD"; 

/**
 * تابع کمکی برای تبدیل ارقام فارسی/عربی به انگلیسی و تبدیل اسلش به خط تیره.
 */
const persianToEnglishNumbers = (str) => {
    if (!str) return '';
    let output = String(str).trim(); 

    const persianNumbers = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicNumbers = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    for (let i = 0; i < 10; i++) {
        output = output.replace(persianNumbers[i], englishNumbers[i]);
        output = output.replace(arabicNumbers[i], englishNumbers[i]);
    }

    // جایگزینی اسلش با خط تیره
    return output.replace(/\//g, "-"); 
};

// =================================================================
/**
 * آداپتور سفارشی PersianDatePickerAdapter
 */
const PersianDatePickerAdapter = ({ value, onChange }) => {
    const [dateValue, setDateValue] = useState(null);

    const toDateObject = (dateString) => {
        if (!dateString) return null;
        const englishDateString = persianToEnglishNumbers(dateString).replace(/\//g, "-"); 
        
        return new DateObject({
            calendar: persian,
            locale: persian_fa,
            date: englishDateString,
            format: SERVER_DATE_FORMAT 
        });
    };
    
    useEffect(() => {
        setDateValue(toDateObject(value));
    }, [value]);

    const handleOnChange = (dateObject) => {
        setDateValue(dateObject); 

        let dateString = null;
        if (dateObject && !Array.isArray(dateObject)) {
            // اطمینان از خروجی استاندارد YYYY-MM-DD با ارقام انگلیسی و خط تیره
            dateString = persianToEnglishNumbers(dateObject.format(SERVER_DATE_FORMAT));
        }
        
        // ارسال مقدار رشته‌ای تمیز و انگلیسی (با خط تیره) به Form.Item
        onChange(dateString);
    };

    return (
        <DatePicker
            calendar={persian}
            locale={persian_fa}
            value={dateValue}
            onChange={handleOnChange}
            inputClass="ant-input" 
            calendarPosition="bottom-right"
            format={SERVER_DATE_FORMAT} 
            placeholder="تاریخ تولد"
            style={{ width: '100%' }}
        />
    );
};
// =================================================================

export const RegisterComponent = ({ setLoading, showMessage, mobile }) => {
    const contextValue = useMemo(() => ({ name: 'Ant Design' }), []);
    const [notify, contextHolder] = notification.useNotification();
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const navigate = useNavigate();
    const fileInputRef = useRef();
    
    const [form] = Form.useForm();

    // اعتبارسنجی‌ها
    const validateNationalCode = (_, value) => {
        if (!value) return Promise.reject(new Error('کد ملی الزامی است!'));
        if (!/^\d{10}$/.test(value)) return Promise.reject(new Error('کد ملی باید دقیقاً ۱۰ رقم باشد!'));
        
        const checkNationalCode = (code) => {
            if (code.length !== 10 || /(\d)(\1){9}/.test(code)) return false;
            let sum = 0;
            for (let i = 0; i < 9; i++) {
                sum += parseInt(code[i]) * (10 - i);
            }
            let remainder = sum % 11;
            let controlDigit = remainder < 2 ? remainder : 11 - remainder;
            return controlDigit === parseInt(code[9]);
        };
        
        if (!checkNationalCode(value)) return Promise.reject(new Error('کد ملی معتبر نیست!'));
        return Promise.resolve();
    };

    const validateTel = (_, value) => {
        if (!value) return Promise.reject(new Error('تلفن ثابت الزامی است!'));
        if (!/^0\d{2,3}\d{7,8}$/.test(value)) return Promise.reject(new Error('فرمت تلفن ثابت معتبر نیست (مثال: ۰۳۱۲۳۴۵۶۷۸۹)'));
        return Promise.resolve();
    };

    const validatePostalCode = (_, value) => {
        if (!value) return Promise.reject(new Error('کد پستی الزامی است!'));
        if (!/^\d{10}$/.test(value)) return Promise.reject(new Error('کد پستی باید دقیقاً ۱۰ رقم باشد!'));
        return Promise.resolve();
    };

    const validateAddress = (_, value) => {
        if (!value) return Promise.reject(new Error('آدرس الزامی است!'));
        if (value.trim().length < 8) return Promise.reject(new Error('آدرس باید حداقل ۸ کاراکتر باشد!'));
        return Promise.resolve();
    };

    const validateState = (_, value) => {
        if (!stateId) return Promise.reject(new Error('انتخاب استان الزامی است!'));
        return Promise.resolve();
    };

    const validateCity = (_, value) => {
        if (!cityId) return Promise.reject(new Error('انتخاب شهر الزامی است!'));
        return Promise.resolve();
    };

    const onSubmit = async (values) => {
        try {
            console.log('مقادیر فرم:', values);
            console.log('شهر انتخاب شده:', cityId);

            // اعتبارسنجی فیلدهای ضروری
            if (!values.shamsiBirthDate) {
                showMessage("لطفاً تاریخ تولد را انتخاب کنید.", "error");
                return;
            }

            if (!cityId) {
                showMessage("لطفاً شهر تولد را انتخاب کنید.", "error");
                return;
            }

            // آماده‌سازی داده‌ها برای ارسال
            const submitData = {
                ...values,
                mobile: mobile,
                isActive: true,
                birthPlaceId: cityId,
                fatherName: values.fatherName || ''
            };

            console.log('داده‌های ارسالی به سرور:', submitData);

            setLoading(true);

            const formData = new FormData();
            if (file) {
                formData.append("photo", file);
            }

            // اضافه کردن تمام فیلدها به FormData
            Object.entries(submitData).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    formData.append(key, value.toString());
                }
            });

            console.log('FormData contents:');
            for (let [key, value] of formData.entries()) {
                console.log(key, value);
            }

            let data = await AddUser_User_Api(formData);
            setLoading(false);

            if (data.messageStatus === "Successful") {
                if (data.additional) {
                    localStorage.setItem('token', data.additional);
                    let rolData = await GetCurrentRole_User_Api();
                    localStorage.setItem('role', rolData.messageData.data);
                }
                showMessage(data.message, "success");
                navigate("/admin");
            } else {
                showMessage(data.message || "خطا در ثبت نام", "error");
            }
        } catch (error) {
            setLoading(false);
            console.error('خطای کامل:', error);
            console.error('پاسخ سرور:', error.response?.data);
            
            if (error.response?.data) {
                showMessage(error.response.data.message || "خطا در ارتباط با سرور", "error");
            } else {
                showMessage("خطا در ارتباط با سرور", "error");
            }
        }
    };

    const handleFileChange = (info) => {
        if (info.fileList.length > 0) {
            const selectedFile = info.fileList[0].originFileObj;
            if (selectedFile.size > 2 * 1024 * 1024) {
                showMessage("حجم فایل باید کمتر از ۲MB باشد", "error");
                return;
            }
            setFile(selectedFile);
            
            const reader = new FileReader();
            reader.onload = (e) => setFilePreview(e.target.result);
            reader.readAsDataURL(selectedFile);
        } else {
            setFile(null);
            setFilePreview(null);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFilePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // کامپوننت Label با آیکون راهنما
    const LabelWithHelp = ({ label, helpText }) => (
        <Space size="small">
            <span>{label}</span>
            <Tooltip title={helpText}>
                <QuestionCircleOutlined style={{ color: '#1890ff', fontSize: '12px', cursor: 'pointer' }} />
            </Tooltip>
        </Space>
    );

    return (
        <Context.Provider value={contextValue}>
            {contextHolder}

            <Form
                form={form}
                name="registerForm"
                wrapperCol={{ span: 24 }}
                onFinish={onSubmit}
                autoComplete="off"
                layout="vertical"
                size="large"
            >
                <Card 
                    style={{ 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 20px rgba(34,35,58,0.1)',
                        maxWidth: '1100px',
                        margin: '0 auto',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {/* تصویر کوچک در گوشه بالا-چپ */}
                    <div style={{
                        position: 'absolute',
                        top: '15px',
                        left: '15px',
                        zIndex: 10
                    }}>
                        <img
                            src={loginImage}
                            width={60}
                            height={45}
                            style={{
                                borderRadius: '6px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                border: '2px solid #fff'
                            }}
                            alt="لوگو"
                        />
                    </div>

                    {/* عنوان فرم */}
                    <div style={{ 
                        textAlign: 'center', 
                        marginBottom: '24px',
                        paddingTop: '10px'
                    }}>
                        <Space direction="horizontal" size="middle">
                            <IdcardOutlined style={{ color: '#1890ff', fontSize: '24px' }} />
                            <span style={{ 
                                fontSize: '20px', 
                                fontWeight: 'bold',
                                color: '#1890ff'
                            }}>
                                فرم ثبت نام
                            </span>
                            <Tooltip title="لطفاً تمام اطلاعات خواسته شده را با دقت وارد کنید">
                                <QuestionCircleOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                            </Tooltip>
                        </Space>
                    </div>

                    {/* بخش فیلدهای فرم - 3 ستونه در دسکتاپ */}
                    <Row gutter={[24, 16]}>
                        {/* ستون اول - اطلاعات شخصی */}
                        <Col xs={24} md={8}>
                            <Space direction="horizontal" style={{ marginBottom: 16 }}>
                                <IdcardOutlined style={{ color: '#52c41a' }} />
                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>اطلاعات شخصی</span>
                            </Space>
                            
                            <Form.Item
                                label={<LabelWithHelp label="نام" helpText="نام خود را به فارسی وارد کنید" />}
                                name="name"
                                rules={[{ required: true, message: 'لطفاً نام خود را وارد کنید!' }]}
                            >
                                <Input placeholder="مثال: علی" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="نام خانوادگی" helpText="نام خانوادگی خود را به فارسی وارد کنید" />}
                                name="family"
                                rules={[{ required: true, message: 'لطفاً نام خانوادگی خود را وارد کنید!' }]}
                            >
                                <Input placeholder="مثال: محمدی" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="نام پدر" helpText="نام پدر خود را به فارسی وارد کنید" />}
                                name="fatherName"
                                rules={[{ required: true, message: 'لطفاً نام پدر خود را وارد کنید!' }]}
                            >
                                <Input placeholder="مثال: رضا" />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="کد ملی" helpText="کد ملی باید ۱۰ رقم و معتبر باشد" />}
                                name="nationalCode"
                                rules={[{ validator: validateNationalCode }]}
                            >
                                <Input 
                                    placeholder="مثال: ۰۰۱۲۳۴۵۶۷۸" 
                                    maxLength={10}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        {/* ستون دوم - اطلاعات تماس */}
                        <Col xs={24} md={8}>
                            <Space direction="horizontal" style={{ marginBottom: 16 }}>
                                <PhoneOutlined style={{ color: '#1890ff' }} />
                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>اطلاعات تماس</span>
                            </Space>
                            
                            <Form.Item
                                label={<LabelWithHelp label="تلفن ثابت" helpText="شماره تلفن ثابت با پیش‌شهر (مثال: ۰۳۱۲۳۴۵۶۷۸۹)" />}
                                name="tel"
                                rules={[{ validator: validateTel }]}
                            >
                                <Input 
                                    placeholder="مثال: ۰۲۱۲۳۴۵۶۷۸۹" 
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                label="شماره موبایل"
                                name="mobile"
                            >
                                <Input 
                                    placeholder="شماره موبایل" 
                                    disabled 
                                    defaultValue={mobile} 
                                    style={{ color: '#666' }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="تاریخ تولد" helpText="تاریخ تولد خود را به شمسی انتخاب کنید" />}
                                name="shamsiBirthDate"
                                rules={[{ required: true, message: 'تاریخ تولد الزامی است!' }]}
                            >
                                <PersianDatePickerAdapter />
                            </Form.Item>
                        </Col>

                        {/* ستون سوم - اطلاعات آدرس و استان/شهر */}
                        <Col xs={24} md={8}>
                            <Space direction="horizontal" style={{ marginBottom: 16 }}>
                                <HomeOutlined style={{ color: '#fa8c16' }} />
                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>اطلاعات آدرس</span>
                            </Space>
                            
                            <Form.Item
                                label={<LabelWithHelp label="استان تولد" helpText="استان محل تولد خود را انتخاب کنید" />}
                                rules={[{ validator: validateState }]}
                            >
                                <CustomTypeCombo
                                    setLoading={setLoading}
                                    showMessage={showMessage}
                                    placeHolder="انتخاب استان"
                                    parentId={2}
                                    onChange={setStateId}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="شهر تولد" helpText="شهر محل تولد خود را انتخاب کنید" />}
                                rules={[{ validator: validateCity }]}
                            >
                                <CustomTypeCombo
                                    setLoading={setLoading}
                                    showMessage={showMessage}
                                    placeHolder="انتخاب شهر"
                                    parentId={stateId}
                                    onChange={setCityId}
                                    disabled={!stateId}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="کد پستی" helpText="کد پستی باید ۱۰ رقم باشد" />}
                                name="postalCode"
                                rules={[{ validator: validatePostalCode }]}
                            >
                                <Input 
                                    placeholder="مثال: ۱۲۳۴۵۶۷۸۹۰" 
                                    maxLength={10}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<LabelWithHelp label="آدرس کامل" helpText="آدرس کامل خود را با حداقل ۸ کاراکتر وارد کنید" />}
                                name="address"
                                rules={[{ validator: validateAddress }]}
                            >
                                <TextArea 
                                    placeholder="مثال: تهران، خیابان ولیعصر، پلاک ۱۲۳"
                                    autoSize={{ minRows: 2, maxRows: 3 }}
                                    showCount
                                    maxLength={500}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '16px 0' }} />

                    {/* بخش تصویر پروفایل کاربر - بهینه‌شده برای موبایل */}
                    <Row gutter={[24, 16]} style={{ marginTop: 8 }}>
                        <Col span={24}>
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                {/* آواتار و دکمه‌ها در یک خط در دسکتاپ، در چند خط در موبایل */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center'
                                }}>
                                    <Avatar
                                        size={60}
                                        src={filePreview}
                                        icon={!filePreview && <UserOutlined />}
                                        style={{ 
                                            border: '2px dashed #d9d9d9',
                                            backgroundColor: filePreview ? 'transparent' : '#f5f5f5',
                                            cursor: 'pointer',
                                            flexShrink: 0
                                        }}
                                        onClick={() => fileInputRef.current?.click()}
                                    />
                                    
                                    <Space 
                                        direction="vertical" 
                                        size="small"
                                        style={{ textAlign: 'center' }}
                                    >
                                        <Space 
                                            direction={{ xs: 'vertical', sm: 'horizontal' }}
                                            size="small"
                                        >
                                            <Upload
                                                ref={fileInputRef}
                                                beforeUpload={() => false}
                                                onChange={handleFileChange}
                                                accept="image/*"
                                                maxCount={1}
                                                showUploadList={false}
                                            >
                                                <Button icon={<UploadOutlined />} type="dashed" size="small">
                                                    انتخاب تصویر پروفایل
                                                </Button>
                                            </Upload>
                                            {filePreview && (
                                                <Space 
                                                    direction={{ xs: 'vertical', sm: 'horizontal' }}
                                                    size="small"
                                                >
                                                    <Button 
                                                        icon={<EyeOutlined />} 
                                                        size="small"
                                                        onClick={() => window.open(filePreview)}
                                                    >
                                                        مشاهده
                                                    </Button>
                                                    <Button 
                                                        icon={<DeleteOutlined />} 
                                                        danger 
                                                        size="small"
                                                        onClick={handleRemoveFile}
                                                    >
                                                        حذف
                                                    </Button>
                                                </Space>
                                            )}
                                        </Space>
                                    </Space>
                                </div>
                                
                                {/* متن راهنما */}
                                <div style={{ 
                                    fontSize: '12px', 
                                    color: '#999', 
                                    lineHeight: '1.3',
                                    textAlign: 'center',
                                    maxWidth: '300px'
                                }}>
                                    فرمت‌های مجاز: JPG, PNG, GIF - حداکثر سایز: ۲MB
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* دکمه ثبت نام */}
                    <Row justify="center" style={{ marginTop: 24 }}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Tooltip title="پس از تکمیل تمام اطلاعات، فرم را ثبت کنید">
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        size="large"
                                        style={{ 
                                            width: '100%',
                                            height: '45px',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            borderRadius: '8px'
                                        }}
                                        icon={<UserOutlined />}
                                    >
                                        ثبت نام نهایی
                                    </Button>
                                </Tooltip>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Context.Provider>
    );
};




/*import React, { useMemo, useState, useRef } from 'react'
import { 
    Button, 
    Col, 
    Form, 
    Input, 
    notification, 
    Row, 
    Upload, 
    Card, 
    Avatar, 
    Space, 
    Tooltip,
    Steps,
    Divider,
    Alert
} from 'antd';
import { AddUser_User_Api, GetCurrentRole_User_Api } from '../../API/userApi';
import { useNavigate } from "react-router-dom";
import {
    UploadOutlined,
    DeleteOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    UserOutlined,
    SolutionOutlined,
    IdcardOutlined,
    HomeOutlined
} from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';
// =================================================================
import DatePicker from 'react-multi-date-picker';
import persian_fa from "react-date-object/locales/persian_fa";
import 'react-multi-date-picker/styles/colors/teal.css';
// =================================================================

const { TextArea } = Input;
const { Step } = Steps;
const Context = React.createContext({ name: 'Default' });

// استایل سفارشی برای DatePicker
const customStyles = `
    .custom-rmdp-input {
        width: 100%;
        height: 32px;
        padding: 4px 11px;
        font-size: 14px;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        transition: all 0.3s;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
        text-align: right;
        direction: rtl;
    }
    
    .custom-rmdp-input:hover {
        border-color: #40a9ff;
    }
    
    .custom-rmdp-input:focus {
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        outline: 0;
    }
    
    /* بهبود ظاهر برای موبایل */
  /*  @media (max-width: 768px) {
        .custom-rmdp-input {
            font-size: 16px; /* جلوگیری از زوم در iOS */
     /*       height: 44px;
        }
    }
`;

export const RegisterComponent = ({ setLoading, showMessage, mobile }) => {
    const contextValue = useMemo(() => ({ name: 'Ant Design' }), []);
    const [notify, contextHolder] = notification.useNotification();
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const navigate = useNavigate();
    const fileInputRef = useRef();
    const formRef = useRef();
    // =================================================================
    const [birthDate, setBirthDate] = useState();
    const [form] = Form.useForm();
    // =================================================================

    // اعتبارسنجی‌ها (همانند کد قبلی)
    const validateNationalCode = (_, value) => {
        if (!value) return Promise.reject(new Error('کد ملی الزامی است!'));
        if (!/^\d{10}$/.test(value)) return Promise.reject(new Error('کد ملی باید دقیقاً 10 رقم باشد!'));
        
        const checkNationalCode = (code) => {
            if (code.length !== 10 || /(\d)(\1){9}/.test(code)) return false;
            let sum = 0;
            for (let i = 0; i < 9; i++) {
                sum += parseInt(code[i]) * (10 - i);
            }
            let remainder = sum % 11;
            let controlDigit = remainder < 2 ? remainder : 11 - remainder;
            return controlDigit === parseInt(code[9]);
        };
        
        if (!checkNationalCode(value)) return Promise.reject(new Error('کد ملی معتبر نیست!'));
        return Promise.resolve();
    };

    const validateTel = (_, value) => {
        if (!value) return Promise.reject(new Error('تلفن ثابت الزامی است!'));
        if (!/^0\d{2,3}\d{7,8}$/.test(value)) return Promise.reject(new Error('فرمت تلفن ثابت معتبر نیست'));
        return Promise.resolve();
    };

    const validatePostalCode = (_, value) => {
        if (!value) return Promise.reject(new Error('کد پستی الزامی است!'));
        if (!/^\d{10}$/.test(value)) return Promise.reject(new Error('کد پستی باید دقیقاً 10 رقم باشد!'));
        return Promise.resolve();
    };

    const validateAddress = (_, value) => {
        if (!value) return Promise.reject(new Error('آدرس الزامی است!'));
        if (value.trim().length < 8) return Promise.reject(new Error('آدرس باید حداقل 8 کاراکتر باشد!'));
        return Promise.resolve();
    };

    const validateBirthDate = (_, value) => {
        if (!birthDate) return Promise.reject(new Error('تاریخ تولد الزامی است!'));
        return Promise.resolve();
    };

    // بررسی اعتبار step جاری
    const validateCurrentStep = async () => {
        try {
            const values = await form.validateFields(
                stepFields[currentStep]
            );
            return true;
        } catch (error) {
            return false;
        }
    };

    // فیلدهای هر step
    const stepFields = [
        ['name', 'family', 'fatherName', 'nationalCode'],
        ['tel', 'mobile', 'shamsiBirthDate'],
        ['postalCode', 'address'],
        [] // تصویر پروفایل
    ];

    const steps = [
        {
            title: 'اطلاعات شخصی',
            icon: <IdcardOutlined />,
            description: 'اطلاعات هویتی'
        },
        {
            title: 'اطلاعات تماس',
            icon: <SolutionOutlined />,
            description: 'راه‌های ارتباطی'
        },
        {
            title: 'آدرس',
            icon: <HomeOutlined />,
            description: 'اطلاعات محل سکونت'
        },
        {
            title: 'تصویر پروفایل',
            icon: <UserOutlined />,
            description: 'عکس پرسنلی'
        }
    ];

    const nextStep = async () => {
        const isValid = await validateCurrentStep();
        if (isValid && currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const onSubmit = async (values) => {
        try {
            if (!birthDate) {
                showMessage("یک تاریخ تولد معتبر وارد کنید.", "error");
                return;
            }

            values.mobile = mobile;
            values.shamsiBirthDate = birthDate;
            values.isActive = true;
            values.birthPlaceId = cityId;
            values.fatherName = values.fatherName || '';

            setLoading(true);

            const formData = new FormData();
            if (file) formData.append("photo", file);

            Object.entries(values).forEach(([key, value]) => {
                formData.append(key, value);
            });

            let data = await AddUser_User_Api(formData);
            setLoading(false);

            if (data.messageStatus === "Successful") {
                if (data.additional) {
                    localStorage.setItem('token', data.additional);
                    let rolData = await GetCurrentRole_User_Api();
                    localStorage.setItem('role', rolData.messageData.data);
                }
                showMessage(data.message, "success");
                navigate("/admin");
            } else {
                showMessage(data.message, "error");
            }
        } catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور", "error");
        }
    };

    const handleFileChange = (info) => {
        if (info.fileList.length > 0) {
            const selectedFile = info.fileList[0].originFileObj;
            // بررسی سایز فایل (2MB)
            if (selectedFile.size > 2 * 1024 * 1024) {
                showMessage("حجم فایل باید کمتر از 2MB باشد", "error");
                return;
            }
            setFile(selectedFile);
            
            const reader = new FileReader();
            reader.onload = (e) => setFilePreview(e.target.result);
            reader.readAsDataURL(selectedFile);
        } else {
            setFile(null);
            setFilePreview(null);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFilePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDateChange = (dateObject) => {
        if (dateObject) {
            const dateString = dateObject.format('YYYY/MM/DD');
            setBirthDate(dateString);
        } else {
            setBirthDate(null);
        }
    };

    // اضافه کردن استایل به head
    React.useEffect(() => {
        const styleSheet = document.createElement("style");
        styleSheet.innerText = customStyles;
        document.head.appendChild(styleSheet);
        
        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="نام"
                                name="name"
                                rules={[{ required: true, message: 'لطفاً نام خود را وارد کنید!' }]}
                            >
                                <Input placeholder="مثال: علی" size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="نام خانوادگی"
                                name="family"
                                rules={[{ required: true, message: 'لطفاً نام خانوادگی خود را وارد کنید!' }]}
                            >
                                <Input placeholder="مثال: محمدی" size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="نام پدر"
                                name="fatherName"
                                rules={[{ required: true, message: 'لطفاً نام پدر خود را وارد کنید!' }]}
                            >
                                <Input placeholder="مثال: رضا" size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="کد ملی"
                                name="nationalCode"
                                rules={[{ validator: validateNationalCode }]}
                            >
                                <Input 
                                    placeholder="مثال: 0012345678" 
                                    maxLength={10}
                                    size="large"
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                );
            
            case 1:
                return (
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="تلفن ثابت"
                                name="tel"
                                rules={[{ validator: validateTel }]}
                            >
                                <Input 
                                    placeholder="مثال: 02123456789" 
                                    size="large"
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="شماره موبایل"
                                name="mobile"
                            >
                                <Input 
                                    placeholder="شماره موبایل" 
                                    disabled 
                                    defaultValue={mobile} 
                                    size="large"
                                    style={{ color: '#666' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="استان تولد"
                            >
                                <CustomTypeCombo
                                    setLoading={setLoading}
                                    showMessage={showMessage}
                                    placeHolder="انتخاب استان"
                                    parentId={2}
                                    onChange={setStateId}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="شهر تولد"
                            >
                                <CustomTypeCombo
                                    setLoading={setLoading}
                                    showMessage={showMessage}
                                    placeHolder="انتخاب شهر"
                                    parentId={stateId}
                                    onChange={setCityId}
                                    disabled={!stateId}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                label="تاریخ تولد"
                                name="shamsiBirthDate"
                                rules={[{ validator: validateBirthDate }]}
                            >
                                <DatePicker
                                    locale={persian_fa}
                                    calendar="persian"
                                    value={birthDate}
                                    onChange={handleDateChange}
                                    format="YYYY/MM/DD"
                                    placeholder="انتخاب تاریخ تولد"
                                    inputClass="custom-rmdp-input"
                                    containerStyle={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                );
            
            case 2:
                return (
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="کد پستی"
                                name="postalCode"
                                rules={[{ validator: validatePostalCode }]}
                            >
                                <Input 
                                    placeholder="مثال: 1234567890" 
                                    maxLength={10}
                                    size="large"
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                label="آدرس کامل"
                                name="address"
                                rules={[{ validator: validateAddress }]}
                            >
                                <TextArea 
                                    placeholder="مثال: تهران، خیابان ولیعصر، پلاک 123"
                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                    showCount
                                    maxLength={500}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                );
            
            case 3:
                return (
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Avatar
                                        size={120}
                                        src={filePreview}
                                        icon={!filePreview && <UserOutlined />}
                                        style={{ 
                                            border: '2px dashed #d9d9d9',
                                            backgroundColor: filePreview ? 'transparent' : '#f5f5f5'
                                        }}
                                    />
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Upload
                                            ref={fileInputRef}
                                            beforeUpload={() => false}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            maxCount={1}
                                            showUploadList={false}
                                        >
                                            <Button icon={<UploadOutlined />} type="dashed" size="large">
                                                انتخاب تصویر پروفایل
                                            </Button>
                                        </Upload>
                                        {filePreview && (
                                            <Space>
                                                <Button 
                                                    icon={<EyeOutlined />} 
                                                    onClick={() => window.open(filePreview)}
                                                >
                                                    مشاهده
                                                </Button>
                                                <Button 
                                                    icon={<DeleteOutlined />} 
                                                    danger 
                                                    onClick={handleRemoveFile}
                                                >
                                                    حذف
                                                </Button>
                                            </Space>
                                        )}
                                    </Space>
                                    <Alert 
                                        message="توجه" 
                                        description="فرمت‌های مجاز: JPG, PNG, GIF - حداکثر سایز: 2MB"
                                        type="info" 
                                        showIcon 
                                        style={{ textAlign: 'right' }}
                                    />
                                </Space>
                            </div>
                        </Col>
                    </Row>
                );
            
            default:
                return null;
        }
    };

    return (
        <Context.Provider value={contextValue}>
            {contextHolder}

            <Form
                form={form}
                name="registerForm"
                wrapperCol={{ span: 24 }}
                onFinish={onSubmit}
                autoComplete="off"
                layout="vertical"
                size="large"
            >
                <Card 
                    title={
                        <Space>
                            <UserOutlined />
                            فرم ثبت نام
                        </Space>
                    }
                    style={{ 
                        borderRadius: '12px', 
                        boxShadow: '0 4px 20px rgba(34,35,58,0.1)',
                        margin: '10px 0'
                    }}
                    extra={
                        <Tooltip title="پر کردن فیلدهای الزامی ضروری است">
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                    }
                >
                    {/* Steps *//*}
                    <Steps 
                        current={currentStep} 
                        style={{ marginBottom: 32 }}
                        responsive={false}
                    >
                        {steps.map((step, index) => (
                            <Step 
                                key={index}
                                title={step.title}
                                description={step.description}
                                icon={step.icon}
                            />
                        ))}
                    </Steps>

                    <Divider />

                    {/* Step Content *//*}
                    <div style={{ minHeight: '300px' }}>
                        {renderStepContent()}
                    </div>

                    {/* Navigation Buttons *//*}
                    <Row justify="space-between" style={{ marginTop: 32 }}>
                        <Col>
                            {currentStep > 0 && (
                                <Button 
                                    size="large" 
                                    onClick={prevStep}
                                    style={{ minWidth: '100px' }}
                                >
                                    مرحله قبل
                                </Button>
                            )}
                        </Col>
                        <Col>
                            {currentStep < steps.length - 1 ? (
                                <Button 
                                    type="primary" 
                                    size="large" 
                                    onClick={nextStep}
                                    style={{ minWidth: '100px' }}
                                >
                                    مرحله بعد
                                </Button>
                            ) : (
                                <Button 
                                    type="primary" 
                                    size="large" 
                                    htmlType="submit"
                                    style={{ minWidth: '150px' }}
                                    icon={<UserOutlined />}
                                >
                                    ثبت نام نهایی
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Context.Provider>
    );
};
*/

//------------------------------------

/*
import React, { useMemo, useState, useRef } from 'react'
import { Button, Col, Form, Input, notification, Row, Upload, Card, Avatar, Space, Tooltip } from 'antd';
import { AddUser_User_Api, GetCurrentRole_User_Api } from '../../API/userApi';
import { useNavigate } from "react-router-dom";
import {
    UploadOutlined,
    DeleteOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    UserOutlined
} from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';
// =================================================================
import DatePicker from 'react-multi-date-picker';
import persian_fa from "react-date-object/locales/persian_fa";
import 'react-multi-date-picker/styles/colors/teal.css';
// =================================================================
const { TextArea } = Input;
const Context = React.createContext({ name: 'Default' });
// =================================================================

// استایل سفارشی برای DatePicker
const customStyles = `
    .custom-rmdp-input {
        width: 100%;
        height: 32px;
        padding: 4px 11px;
        font-size: 14px;
        border: 1px solid #d9d9d9;
        border-radius: 6px;
        transition: all 0.3s;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
    }
    
    .custom-rmdp-input:hover {
        border-color: #40a9ff;
    }
    
    .custom-rmdp-input:focus {
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        outline: 0;
    }
`;

// اضافه کردن استایل به head
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
}

export const RegisterComponent = ({ setLoading, showMessage, mobile }) => {
    const contextValue = useMemo(() => ({ name: 'Ant Design' }), []);
    const [notify, contextHolder] = notification.useNotification();
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const navigate = useNavigate();
    const fileInputRef = useRef();
    // =================================================================
    const [birthDate, setBirthDate] = useState();
    const [form] = Form.useForm();
    // =================================================================

    // اعتبارسنجی کد ملی
    const validateNationalCode = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('کد ملی الزامی است!'));
        }
        
        if (!/^\d{10}$/.test(value)) {
            return Promise.reject(new Error('کد ملی باید دقیقاً 10 رقم باشد!'));
        }
        
        // الگوریتم اعتبارسنجی کد ملی
        const checkNationalCode = (code) => {
            if (code.length !== 10 || /(\d)(\1){9}/.test(code)) return false;
            
            let sum = 0;
            for (let i = 0; i < 9; i++) {
                sum += parseInt(code[i]) * (10 - i);
            }
            let remainder = sum % 11;
            let controlDigit = remainder < 2 ? remainder : 11 - remainder;
            
            return controlDigit === parseInt(code[9]);
        };
        
        if (!checkNationalCode(value)) {
            return Promise.reject(new Error('کد ملی معتبر نیست!'));
        }
        
        return Promise.resolve();
    };

    // اعتبارسنجی تلفن ثابت
    const validateTel = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('تلفن ثابت الزامی است!'));
        }
        
        if (!/^0\d{2,3}\d{7,8}$/.test(value)) {
            return Promise.reject(new Error('فرمت تلفن ثابت معتبر نیست (مثال: 03123456789)'));
        }
        
        return Promise.resolve();
    };

    // اعتبارسنجی کد پستی
    const validatePostalCode = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('کد پستی الزامی است!'));
        }
        
        if (!/^\d{10}$/.test(value)) {
            return Promise.reject(new Error('کد پستی باید دقیقاً 10 رقم باشد!'));
        }
        
        return Promise.resolve();
    };

    // اعتبارسنجی آدرس
    const validateAddress = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('آدرس الزامی است!'));
        }
        
        if (value.trim().length < 8) {
            return Promise.reject(new Error('آدرس باید حداقل 8 کاراکتر باشد!'));
        }
        
        return Promise.resolve();
    };

    // اعتبارسنجی تاریخ تولد
    const validateBirthDate = (_, value) => {
        if (!birthDate) {
            return Promise.reject(new Error('تاریخ تولد الزامی است!'));
        }
        
        return Promise.resolve();
    };

    const onSubmit = async (values) => {
        try {
            values.mobile = mobile;
            if (!birthDate) {
                showMessage("یک تاریخ تولد معتبر وارد کنید.", "error");
                return;
            }
            values.shamsiBirthDate = birthDate;
            values.isActive = true;
            values.birthPlaceId = cityId;
            
            if (!values.fatherName)
                values.fatherName = '';
            setLoading(true);

            const formData = new FormData();
            if (file)
                formData.append("photo", file);

            Object.entries(values).forEach(([key, value]) => {
                formData.append(key, value);
            });

            let data = await AddUser_User_Api(formData);
            setLoading(false);

            if (data.messageStatus == "Successful") {
                if (data.additional) {
                    localStorage.setItem('token', data.additional);
                    let rolData = await GetCurrentRole_User_Api();
                    localStorage.setItem('role', rolData.messageData.data);
                }
                showMessage(data.message, "Successful");
                navigate("/admin");
            }
            else {
                showMessage(data.message, "error");
            }
        }
        catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور", "error")
        }
    };

    const handleFileChange = (info) => {
        if (info.fileList.length > 0) {
            const selectedFile = info.fileList[0].originFileObj;
            setFile(selectedFile);
            
            // ایجاد پیش‌نمایش تصویر
            const reader = new FileReader();
            reader.onload = (e) => {
                setFilePreview(e.target.result);
            };
            reader.readAsDataURL(selectedFile);
        } else {
            setFile(null);
            setFilePreview(null);
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setFilePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDateChange = (dateObject) => {
        if (dateObject) {
            const dateString = dateObject.format('YYYY/MM/DD');
            setBirthDate(dateString);
            console.log('تاریخ انتخاب شده:', dateString);
        } else {
            setBirthDate(null);
        }
    };

    return (
        <Context.Provider value={contextValue}>
            {contextHolder}

            <Form
                form={form}
                name="basic"
                wrapperCol={{ span: 24 }}
                onFinish={onSubmit}
                autoComplete="off"
                layout="vertical"
            >
                <Card 
                    title="فرم ثبت نام" 
                    className='register-component' 
                    style={{ 
                        borderRadius: '10px', 
                        boxShadow: '0 14px 80px rgba(34,35,58,.2)',
                        margin: '20px 0'
                    }}
                    extra={
                        <Tooltip title="پر کردن فیلدهای ستاره‌دار الزامی است">
                            <InfoCircleOutlined />
                        </Tooltip>
                    }
                >
                    <Row gutter={[16, 16]}>

                        
                        <Col span={24}>
                            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                <Space direction="vertical" size="middle">
                                    <Avatar
                                        size={100}
                                        src={filePreview}
                                        icon={!filePreview && <UserOutlined />}
                                        style={{ 
                                            border: '2px dashed #d9d9d9',
                                            backgroundColor: filePreview ? 'transparent' : '#f5f5f5'
                                        }}
                                    />
                                    <Space>
                                        <Upload
                                            ref={fileInputRef}
                                            beforeUpload={() => false}
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            maxCount={1}
                                            showUploadList={false}
                                        >
                                            <Button icon={<UploadOutlined />} type="dashed">
                                                انتخاب تصویر
                                            </Button>
                                        </Upload>
                                        {filePreview && (
                                            <Space>
                                                <Button 
                                                    icon={<EyeOutlined />} 
                                                    onClick={() => {
                                                        const newWindow = window.open();
                                                        newWindow.document.write(`<img src="${filePreview}" style="max-width: 100%; height: auto;" />`);
                                                    }}
                                                >
                                                    مشاهده
                                                </Button>
                                                <Button 
                                                    icon={<DeleteOutlined />} 
                                                    danger 
                                                    onClick={handleRemoveFile}
                                                >
                                                    حذف
                                                </Button>
                                            </Space>
                                        )}
                                    </Space>
                                    <div style={{ fontSize: '12px', color: '#999' }}>
                                        فرمت‌های مجاز: JPG, PNG, GIF - حداکثر سایز: 2MB
                                    </div>
                                </Space>
                            </div>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="نام"
                                name="name"
                                rules={[{ 
                                    required: true, 
                                    message: 'لطفاً نام خود را وارد کنید!' 
                                }]}
                                tooltip="نام خود را به فارسی وارد کنید"
                            >
                                <Input 
                                    placeholder="مثال: علی" 
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="نام خانوادگی"
                                name="family"
                                rules={[{ 
                                    required: true, 
                                    message: 'لطفاً نام خانوادگی خود را وارد کنید!' 
                                }]}
                                tooltip="نام خانوادگی خود را به فارسی وارد کنید"
                            >
                                <Input 
                                    placeholder="مثال: محمدی" 
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="نام پدر"
                                name="fatherName"
                                rules={[{ 
                                    required: true, 
                                    message: 'لطفاً نام پدر خود را وارد کنید!' 
                                }]}
                                tooltip="نام پدر خود را به فارسی وارد کنید"
                            >
                                <Input 
                                    placeholder="مثال: رضا" 
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="کد ملی"
                                name="nationalCode"
                                rules={[{ 
                                    validator: validateNationalCode 
                                }]}
                                tooltip="کد ملی باید 10 رقم و معتبر باشد"
                            >
                                <Input 
                                    placeholder="مثال: 0012345678" 
                                    maxLength={10}
                                    size="large"
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="تلفن ثابت"
                                name="tel"
                                rules={[{ 
                                    validator: validateTel 
                                }]}
                                tooltip="شماره تلفن ثابت با پیش‌شهر (مثال: 03123456789)"
                            >
                                <Input 
                                    placeholder="مثال: 02123456789" 
                                    size="large"
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="شماره موبایل"
                                name="mobile"
                            >
                                <Input 
                                    placeholder="شماره موبایل" 
                                    disabled 
                                    defaultValue={mobile} 
                                    size="large"
                                    style={{ textAlign: 'center', color: '#666' }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="استان تولد"
                                tooltip="استان محل تولد خود را انتخاب کنید"
                            >
                                <CustomTypeCombo
                                    setLoading={setLoading}
                                    showMessage={showMessage}
                                    placeHolder="انتخاب استان"
                                    parentId={2}
                                    onChange={(value) => { setStateId(value) }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="شهر تولد"
                                tooltip="شهر محل تولد خود را انتخاب کنید"
                            >
                                <CustomTypeCombo
                                    setLoading={setLoading}
                                    showMessage={showMessage}
                                    placeHolder="انتخاب شهر"
                                    parentId={stateId}
                                    onChange={(value) => { setCityId(value) }}
                                    disabled={!stateId}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="تاریخ تولد"
                                name="shamsiBirthDate"
                                rules={[{ 
                                    validator: validateBirthDate 
                                }]}
                                tooltip="تاریخ تولد خود را به شمسی انتخاب کنید"
                            >
                                <DatePicker
                                    locale={persian_fa}
                                    calendar="persian"
                                    value={birthDate}
                                    onChange={handleDateChange}
                                    format="YYYY/MM/DD"
                                    calendarPosition="bottom-right"
                                    placeholder="انتخاب تاریخ تولد"
                                    inputClass="custom-rmdp-input"
                                    hideOnScroll={false}
                                    containerStyle={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="کد پستی"
                                name="postalCode"
                                rules={[{ 
                                    validator: validatePostalCode 
                                }]}
                                tooltip="کد پستی باید 10 رقم باشد"
                            >
                                <Input 
                                    placeholder="مثال: 1234567890" 
                                    maxLength={10}
                                    size="large"
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                    }}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label="آدرس کامل"
                                name="address"
                                rules={[{ 
                                    validator: validateAddress 
                                }]}
                                tooltip="آدرس کامل خود را با حداقل 8 کاراکتر وارد کنید"
                            >
                                <TextArea 
                                    placeholder="مثال: تهران، خیابان ولیعصر، پلاک 123"
                                    autoSize={{ minRows: 3, maxRows: 6 }}
                                    showCount
                                    maxLength={500}
                                    size="large"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item style={{ marginBottom: 0, textAlign: 'left' }}>
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    size="large"
                                    style={{ 
                                        width: '200px',
                                        height: '45px',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    ثبت نام نهایی
                                </Button>
                            </Form.Item>
                        </Col>

                    </Row>
                </Card>
            </Form>
        </Context.Provider>
    )
}
*/



// OK Old
/*
import React, { useMemo, useState } from 'react'
import { Button, Col, Form, Input, notification, Row, Upload } from 'antd';
import { AddUser_User_Api, GetCurrentRole_User_Api } from '../../API/userApi';
import { DatePicker as DatePickerJalali, JalaliLocaleListener } from "antd-jalali";
import { useNavigate } from "react-router-dom";
import {
    UploadOutlined
} from '@ant-design/icons';
import { CustomTypeCombo } from '../CustomType/CustomTypeCombo';
// =================================================================
const { TextArea } = Input;
const Context = React.createContext({ name: 'Default' });
// =================================================================
export const RegisterComponent = ({ setLoading, showMessage, mobile }) => {
    const contextValue = useMemo(() => ({ name: 'Ant Design' }), []);
    const [notify, contextHolder] = notification.useNotification();
    const [file, setFile] = useState(null);
    const [stateId, setStateId] = useState(null);
    const [cityId, setCityId] = useState(null);
    const navigate = useNavigate();
    // =================================================================
    const [birthDate, setbirthDate] = useState();
    // =================================================================
    const onSubmit = async (values) => {
        try {
            values.mobile = mobile;
            if (!birthDate) {
                showMessage("یک تاریخ تولد معتبر وارد کنید.", "error");
                return;
            }
            values.shamsiBirthDate = birthDate;
            values.isActive = true;
            values.birthPlaceId = cityId;
            
            if (!values.fatherName)
                values.fatherName = '';
            setLoading(true);

            //////////////////////////////
            const formData = new FormData();
            if (file)
                formData.append("photo", file);

            //برای person
            Object.entries(values).forEach(([key, value]) => {
                formData.append(key, value);
            });

            let data = await AddUser_User_Api(formData);
            setLoading(false);

            if (data.messageStatus == "Successful") {

                if (data.additional)
                {
                    localStorage.setItem('token', data.additional);
                    let rolData = await GetCurrentRole_User_Api();
                    localStorage.setItem('role', rolData.messageData.data);
                }
                showMessage(data.message, "Successful");
                navigate("/admin");
            }
            else
                showMessage(data.message, "error");
        }
        catch (error) {
            setLoading(false);
            showMessage("خطا در ارتباط با سرور", "error")
        }
    };
    // =================================================================
    const handleFileChange = (info) => {
        // انتخاب اولین فایل
        if (info.fileList.length > 0) {
            setFile(info.fileList[0].originFileObj);
        } else {
            setFile(null);
        }
    };
    // =================================================================
    return (
        <Context.Provider value={contextValue}>
            {contextHolder}

            <Form
                name="basic"
                wrapperCol={{ span: 24 }}
                onFinish={onSubmit}
                autoComplete="off"
            >
                <Row className='register-component' style={{ padding: '15px', borderRadius: '10px', boxShadow: '0 14px 80px rgba(34,35,58,.2)' }}>

                    <Col span={12}>
                        <Form.Item
                            name="name"
                            rules={[{ required: true, message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <Input placeholder="نام" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="family"
                            rules={[{ required: true, message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <Input placeholder='نام خانوادگی' />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="fatherName"
                            rules={[{ required: true, message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <Input placeholder='نام پدر' />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="nationalCode"
                            rules={[{ required: true, message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <Input placeholder='کد ملی' />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="tel"
                            rules={[{ required: true, message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <Input placeholder='تلفن ثابت' />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="mobile"
                            rules={[{ message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <Input style={{ textAlign: 'center' }} placeholder='شماره موبایل' disabled defaultValue={mobile} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <CustomTypeCombo
                            setLoading={setLoading}
                            showMessage={showMessage}
                            placeHolder="استان تولد"
                            parentId={2}
                         onChange={(value) => { setStateId(value) }}
                        />

                    </Col>
                    <Col span={12}>
                    <CustomTypeCombo
                            setLoading={setLoading}
                            showMessage={showMessage}
                            placeHolder="شهر تولد"
                            parentId={stateId}
                         onChange={(value) => { setCityId(value) }}
                        />
                    </Col>

                    <Col span={12}>

                        <Form.Item
                            name="shamsiBirthDate"
                            rules={[{ message: 'یک مقدار معتبر وارد کنید!' }]}

                        >
                            <JalaliLocaleListener />
                            <DatePickerJalali placeholder={"تاریخ تولد"} onChange={(date, dateString) => { setbirthDate(dateString); }} />
                        </Form.Item>
                    </Col>


                    <Col span={12}>
                        <Form.Item
                            name="postalCode"
                            rules={[{ required: true, message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <Input placeholder='کد پستی' />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="address"
                            rules={[{ required: true, message: 'یک مقدار معتبر وارد کنید!' }]}
                        >
                            <TextArea placeholder="آدرس"
                                autoSize={{ minRows: 2, maxRows: 6 }} />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Upload
                            style={{ width: '95%' }}
                            beforeUpload={() => false}
                            onChange={handleFileChange}
                            accept="image/*"
                            maxCount={1}
                        >

                            <Button icon={<UploadOutlined />} style={{ width: '100%' }}>انتخاب تصویر پروفایل</Button>
                        </Upload>
                    </Col>

                    <Col span={24}>
                        <Form.Item >
                            <Button style={{ width: '95%' }} type="primary" htmlType="submit">
                                ثبت نام
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Context.Provider>
    )
}
*/    